// ── 모바일 렌더 영역 (≤768px) ─────────────────────────────────────────────────
// 히어로 헤더 + 서브탭(매장카드/날짜순) + 담당매장·미연결결제 내부 탭

import React, { useState } from 'react';
import { C } from './constants';
import { fmt } from './utils';
import { toDateString } from '../../../utils/date';
import { s } from './mobileStyles';
import { LoadingPlaceholder, EmptyState } from './CommonComponents';
import { PayGroup, TimelineView } from './PayComponents';
import { MobileStoresTab } from './MobileStoresTab';
import { SettlementRecordsModal } from './SettlementRecordsModal';
import { SummaryHeader } from '../../../components/settlement/SummaryHeader';

export function MobileView({
  summary, loading, umLoading,
  clients, records, cancelEvents = [], unmatched, unmatchedRev,
  shareIncome,  // 상담자 배분 수입 { items: [...], total }
  subTab, setSubTab,
  activeTab, setActiveTab,
  expandedId, setExpandedId,
  selectedPay, setSelectedPay,
  dragOverId,
  handleDragStart, handleDragOver, handleDragLeave, handleDrop,
  handlePaymentClick, handleStoreCardClick,
  handleUnlink, fetchSettlement, fetchUnmatched,
  cfg,  // 회사별 설정 — heroLabel, unmatchedCardAmt 등 (없으면 DOT 기본값 사용)
}) {
  const hasSelected = !!selectedPay;
  const recTotal    = records.reduce((acc, r) => acc + Number(r.amount), 0);
  const umTotal     = unmatched.card.length + unmatched.phone.length + unmatched.payments.length;

  // 정산지급 내역 모달 상태
  const [showRecordsModal, setShowRecordsModal] = useState(false);

  // 정산지급 타임라인 아이템 — buildTimeline과 동일한 형태로 변환
  // TimelineItem 컴포넌트 재사용 → 날짜순 모양 수정 시 자동 반영
  const settlementItems = records
    .map((r) => ({
      date: toDateString(r.settlement_date || ''),
      type: 'settlement',
      label: r.note || null,  // 배지에 이미 '정산지급' 표시 — 메모 없으면 null로 중복 방지
      sub: null,
      store: null, origAmt: null,
      amount: -Number(r.amount), matched: true,
      key: `settlement-${r.id}`,
    }))
    .sort((a, b) => (b.date || '').localeCompare(a.date || ''));

  // 새로고침 버튼 (SVG 아이콘)
  const refreshBtn = (
    <button style={s.refreshIconBtn} onClick={() => { fetchSettlement(); fetchUnmatched(); }} title="새로고침">
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="23 4 23 10 17 10" />
        <polyline points="1 20 1 14 7 14" />
        <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
      </svg>
    </button>
  );

  const netAmt = summary.netTotal - recTotal;

  return (
    <div style={s.page}>

      {/* ── 히어로 헤더 ── full-width, 중첩 박스 없이 단일 레이어 */}
      <div style={s.hero}>
        <div style={s.heroInner}>

          {/* 정산받을돈 레이블 + 금액 — 클릭 시 정산지급 내역 모달 */}
          <div
            onClick={() => records.length > 0 && setShowRecordsModal(true)}
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, cursor: records.length > 0 ? 'pointer' : 'default' }}
          >
            <div style={{ ...s.heroNetLabel, display: 'flex', alignItems: 'center', gap: 4 }}>
              {cfg?.heroLabel || '정산받을돈'}
              {/* 클릭 가능 힌트 — 기록 있을 때만 */}
              {records.length > 0 && (
                <span style={{ fontSize: 9, opacity: 0.6 }}>▼</span>
              )}
            </div>
            <div style={{ ...s.heroNetAmt, color: netAmt >= 0 ? '#34d399' : '#f87171' }}>
              {netAmt >= 0 ? '+' : '-'}{fmt(Math.abs(netAmt))}원
            </div>
          </div>

          {/* 차감(받은돈 포함) / 수입 통계 — 공유 컴포넌트 사용 */}
          <SummaryHeader
            totalMinus={summary.totalMinus}
            totalPlus={summary.totalPlus}
            recTotal={recTotal}
            netExpected={netAmt}
            variant="compact"
          />

        </div>
      </div>

      {/* ── 서브탭 (매장카드 / 날짜순) ── */}
      <div style={s.subTabBar}>
        <button
          style={{ ...s.subTab, ...(subTab === 'stores' ? s.subTabActive : {}) }}
          onClick={() => setSubTab('stores')}
        >
          매장카드
        </button>
        <button
          style={{ ...s.subTab, ...(subTab === 'timeline' ? s.subTabActive : {}) }}
          onClick={() => setSubTab('timeline')}
        >
          날짜순
          {umTotal > 0 && (
            <span style={{ ...s.tabBadge, background: C.minus, color: '#fff' }}>{umTotal}</span>
          )}
        </button>
        {refreshBtn}
      </div>

      {/* ══ 매장카드 뷰 ══ */}
      {subTab === 'stores' && (
        <>
          {/* 결제 선택 배너 */}
          {hasSelected && (
            <div style={s.selBanner}>
              <div style={s.selBannerIcon}>↕</div>
              <div style={s.selBannerText}>
                <span style={s.selBannerTitle}>결제 항목 선택됨</span>
                <span style={s.selBannerSub}>연결할 매장 카드를 탭하세요</span>
              </div>
              <button style={s.selCancelBtn} onClick={() => setSelectedPay(null)}>취소</button>
            </div>
          )}

          {/* 내부 탭 (담당매장 / 미연결결제) */}
          <div style={s.tabBar}>
            <button
              style={{ ...s.tab, ...(activeTab === 'stores' ? s.tabActive : {}) }}
              onClick={() => setActiveTab('stores')}
            >
              담당 매장
              {clients.length > 0 && (
                <span style={{ ...s.tabBadge, ...(activeTab === 'stores' ? s.tabBadgeActive : {}) }}>
                  {clients.length}
                </span>
              )}
            </button>
            <button
              style={{ ...s.tab, ...(activeTab === 'unmatched' ? s.tabActive : {}) }}
              onClick={() => setActiveTab('unmatched')}
            >
              미연결 결제
              {umTotal > 0 && (
                <span style={{ ...s.tabBadge, background: C.minus, color: '#fff' }}>{umTotal}</span>
              )}
            </button>
          </div>

          {/* 담당매장 — MobileStoresTab 컴포넌트로 분리 (기타 수입 카드 포함) */}
          {activeTab === 'stores' && (
            <div style={s.tabContent}>
              <MobileStoresTab
                clients={clients}
                loading={loading}
                records={records}
                shareIncome={shareIncome}
                others={unmatchedRev?.others || []}
                consultantCharges={unmatchedRev?.consultantCharges || []}
                expandedId={expandedId}
                setExpandedId={setExpandedId}
                hasSelected={hasSelected}
                dragOverId={dragOverId}
                handleDragOver={handleDragOver}
                handleDragLeave={handleDragLeave}
                handleDrop={handleDrop}
                handleStoreCardClick={handleStoreCardClick}
                handleUnlink={handleUnlink}
              />
            </div>
          )}

          {/* 미연결결제 */}
          {activeTab === 'unmatched' && (
            <div style={s.tabContent}>
              {umLoading && <LoadingPlaceholder />}
              {!umLoading && (
                <>
                  {/* card_upload: LNJ 업로드 카드승인 (ext_revenue_card_approvals) */}
                  <PayGroup title="카드업로드" type="card_upload" items={unmatched.card_upload || []}
                    selectedPay={selectedPay} onDragStart={handleDragStart} onClick={handlePaymentClick}
                    getDate={(i) => i.transaction_date}
                    getLabel={(i) => i.card_issuer || i.merchant_name || '—'}
                    getSub={(i) => i.installment_months > 0 ? `${i.installment_months}개월` : '일시불'}
                    getAmt={cfg?.unmatchedCardAmt || ((i) => i.transaction_amount)} />
                  <PayGroup title="카드승인" type="card" items={unmatched.card}
                    selectedPay={selectedPay} onDragStart={handleDragStart} onClick={handlePaymentClick}
                    getDate={(i) => i.transaction_date}
                    getLabel={(i) => i.card_issuer || i.merchant_name || '—'}
                    getSub={(i) => i.note || null}
                    getAmt={cfg?.unmatchedCardAmt || ((i) => i.amount)} />
                  <PayGroup title="전화승인" type="phone" items={unmatched.phone}
                    selectedPay={selectedPay} onDragStart={handleDragStart} onClick={handlePaymentClick}
                    getDate={(i) => i.payment_date} getLabel={(i) => i.card_company}
                    getSub={(i) => i.note} getAmt={(i) => i.amount} />
                  <PayGroup title="입금" type="payments" items={unmatched.payments}
                    selectedPay={selectedPay} onDragStart={handleDragStart} onClick={handlePaymentClick}
                    getDate={(i) => i.payment_date} getLabel={(i) => i.payer_name}
                    getSub={(i) => i.note} getAmt={(i) => i.amount} />
                  {umTotal === 0 && (
                    <EmptyState icon="✓" text="미연결 결제가 없습니다" sub="모든 결제가 매장에 연결되었습니다" />
                  )}
                </>
              )}
            </div>
          )}
        </>
      )}

      {/* ══ 날짜순 뷰 ══ */}
      {subTab === 'timeline' && (
        <TimelineView
          clients={clients}
          unmatched={{
            card:        unmatchedRev.cards      || [],
            card_upload: unmatchedRev.cardUploads || [],  // DOT·LNJ 업로드 카드 (기존 누락)
            phone:       unmatchedRev.phones     || [],
            payments:    unmatchedRev.deposits   || [],
            others:      unmatchedRev?.others    || [],   // 기타 수입
            consultantCharges: unmatchedRev?.consultantCharges || [],
          }}
          records={records}
          cancelEvents={cancelEvents}
          shareIncome={shareIncome}   // 상담자 배분 수입
          loading={loading || umLoading}
        />
      )}

      {/* ══ 정산지급 내역 모달 — 분리된 컴포넌트 사용 ══ */}
      {showRecordsModal && (
        <SettlementRecordsModal
          records={records}
          recTotal={recTotal}
          settlementItems={settlementItems}
          onClose={() => setShowRecordsModal(false)}
        />
      )}

    </div>
  );
}
